import { useState, useEffect, useMemo } from "react";
import { apiFetch, can } from "../api";
import { Spinner, EmptyState } from "../components/ui";  // UI-SWEEP-DIRECTORY
import EditCompanyModal from "../components/EditCompanyModal";
import { SortableTh, sortRowsBy } from "../components/SortableTable";

// ============================================================================
// Companies page (Chunk 7C / 7G)
// ----------------------------------------------------------------------------
// Master company list. Any logged-in user can create / edit. Non-admin writes
// auto-flip needs_admin_review=true; the edit modal (shared with the Admin
// Review Queue) diffs current values against last_reviewed_snapshot and
// highlights each pending field in amber. Admins see an Approve action on
// pending rows + inside the edit modal.
//
// Chunk 7G: sortable column headers, sticky filter/header bar, and
// click-through navigation callbacks threaded down to the edit modal so
// linked-contact / linked-claim rows can jump to the right page.
// ============================================================================

// Local Modal + parseErr helpers are still used by CreateCompanyModal below.
function Modal({ title, onClose, children, footer }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3>{title}</h3><button className="modal-close" onClick={onClose}>×</button></div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

function parseErr(e, fallback) {
  try { return JSON.parse(e.message).detail || fallback; } catch { return fallback; }
}

export default function Companies({ currentUser, onNavigateToClaim, onNavigateToContact, focusCompanyId, onFocusHandled }) {
  // Chunk 14C: `isAdmin` is a local name kept stable so the prop passes to
  // shared modals (EditCompanyModal, CreateCompanyModal) don't have to change
  // — semantically it now means "has approve_companies capability".
  const isAdmin = can("approve_companies");
  const [companies, setCompanies] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  const [pendingOnly, setPendingOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [sort, setSort] = useState({ key: "company_name", dir: "asc" });

  const scheduleMap = schedules.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

  const load = () => {
    setLoading(true);
    const q = new URLSearchParams();
    if (search.trim()) q.set("search", search.trim());
    if (includeInactive) q.set("include_inactive", "true");
    if (pendingOnly) q.set("needs_review", "true");
    q.set("limit", "5000");  // BUG-10: the default (500) cut the list off at "S"
    const qs = q.toString();
    Promise.all([
      apiFetch(`/companies${qs ? "?" + qs : ""}`),
      apiFetch("/rate-schedules"),
    ])
      .then(([c, s]) => { setCompanies(c); setSchedules(s); setLoading(false); })
      .catch(e => { setErr(parseErr(e, "Failed to load companies")); setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive, pendingOnly]);

  // Deep-link from Directory: open the focused company's modal once loaded.
  useEffect(() => {
    if (!focusCompanyId || !companies.length) return;
    const co = companies.find(c => c.id === focusCompanyId);
    if (co) setEditing(co);
    onFocusHandled && onFocusHandled();
    /* eslint-disable-next-line */
  }, [focusCompanyId, companies]);

  const onSearchKey = (e) => { if (e.key === "Enter") load(); };

  const pendingCount = companies.filter(c => c.needs_admin_review).length;

  const quickApprove = async (company) => {
    if (!window.confirm(`Approve "${company.company_name}" as reviewed?`)) return;
    try {
      await apiFetch(`/companies/${company.id}/approve`, { method:"POST" });
      load();
    } catch (e) { alert(parseErr(e, "Failed to approve")); }
  };

  // Computed column accessors + derived row fields the sort engine can key on.
  const sortAccessors = useMemo(() => ({
    location: c => [c.city, c.state].filter(Boolean).join(", "),
    default_rate_schedule_name: c => scheduleMap[c.default_rate_schedule_id]?.name,
    status_rank: c => c.needs_admin_review ? 1 : (c.is_active ? 0 : 2),  // Active first, then Pending, then Disabled
  }), [scheduleMap]);

  const sorted = sortRowsBy(companies, sort.key, sort.dir, sortAccessors);

  return (
    <div className="page list-view">
      <div className="list-header">
        <div className="section-header">
          <span className="section-title">
            Companies ({companies.length}){pendingCount > 0 && <span style={{marginLeft:10,fontSize:12,color:"#b45309",fontWeight:500}}>— {pendingCount} pending review</span>}
          </span>
          <button className="btn btn-blue btn-sm" onClick={() => setCreating(true)}>+ New Company</button>
        </div>

        <div style={{display:"flex",gap:12,alignItems:"center",marginBottom:12,flexWrap:"wrap"}}>
          <input value={search} onChange={e=>setSearch(e.target.value)} onKeyDown={onSearchKey} placeholder="Search by name... (Enter)" style={{flex:"1 1 240px",maxWidth:400}}/>
          <button className="btn btn-outline btn-sm" onClick={load}>Search</button>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text2)",cursor:"pointer"}}>
            <input type="checkbox" checked={includeInactive} onChange={e=>setIncludeInactive(e.target.checked)}/>
            Include disabled
          </label>
          <label style={{display:"flex",alignItems:"center",gap:6,fontSize:12,color:"var(--text2)",cursor:"pointer"}}>
            <input type="checkbox" checked={pendingOnly} onChange={e=>setPendingOnly(e.target.checked)}/>
            Pending review only
          </label>
        </div>

        {err && <div className="alert-error">{err}</div>}
      </div>

      <div className="list-body card table-wrap">
        {loading ? <Spinner label="Loading companies…" />
         : sorted.length === 0 ? <EmptyState icon="🏢" title="No companies match" hint="Try a different search or clear filters." />
         : (<table>
            <thead><tr>
              <SortableTh label="Name"             col="company_name"                 sort={sort} setSort={setSort}/>
              <SortableTh label="Location"         col="location"                     sort={sort} setSort={setSort}/>
              <SortableTh label="Phone"            col="phone"                        sort={sort} setSort={setSort}/>
              <SortableTh label="Default Schedule" col="default_rate_schedule_name"   sort={sort} setSort={setSort}/>
              <SortableTh label="Status"           col="status_rank"                  sort={sort} setSort={setSort}/>
              <th className="text-right">Actions</th>
            </tr></thead>
            <tbody>{sorted.map(c => {
              const loc = [c.city, c.state].filter(Boolean).join(", ") || "—";
              return (
                <tr key={c.id} style={c.needs_admin_review ? {background:"#fffbeb"} : undefined}>
                  <td style={{fontWeight:600}}>{c.company_name}</td>
                  <td>{loc}</td>
                  <td>{c.phone || "—"}</td>
                  <td>{c.default_rate_schedule_id ? (scheduleMap[c.default_rate_schedule_id]?.name || `#${c.default_rate_schedule_id}`) : <span style={{color:"var(--text3)"}}>—</span>}</td>
                  <td>
                    {!c.is_active && <span className="badge" style={{background:"#f3f4f6",color:"#6b7280",marginRight:4}}>Disabled</span>}
                    {c.needs_admin_review && <span className="badge" style={{background:"#fef3c7",color:"#92400e"}}>Pending Review</span>}
                    {c.is_active && !c.needs_admin_review && <span className="badge" style={{background:"#d1fae5",color:"#065f46"}}>Active</span>}
                  </td>
                  <td className="text-right" style={{whiteSpace:"nowrap"}}>
                    {isAdmin && c.needs_admin_review && (
                      <button className="btn btn-primary btn-sm" onClick={() => quickApprove(c)} style={{marginRight:4}}>Approve</button>
                    )}
                    <button className="btn btn-outline btn-sm" onClick={() => setEditing(c)}>Edit</button>
                  </td>
                </tr>
              );
            })}</tbody>
          </table>)}
      </div>

      {editing && <EditCompanyModal
        company={editing}
        schedules={schedules}
        isAdmin={isAdmin}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
        onNavigateToClaim={onNavigateToClaim}
        onNavigateToContact={onNavigateToContact}
      />}
      {creating && <CreateCompanyModal schedules={schedules} isAdmin={isAdmin} onClose={() => setCreating(false)} onCreated={() => { setCreating(false); load(); }}/>}
    </div>
  );
}

// ============================================================================
// Create modal
// ============================================================================
export function CreateCompanyModal({ schedules, isAdmin, onClose, onCreated }) {
  const [form, setForm] = useState({
    company_name: "",
    city: "",
    state: "",
    phone: "",
    email: "",
    notes: "",
    default_rate_schedule_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    setErr(null);
    if (!form.company_name.trim()) { setErr("Name is required"); return; }
    setSaving(true);
    try {
      await apiFetch("/companies", { method:"POST", body: {
        company_name: form.company_name.trim(),
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
        default_rate_schedule_id: form.default_rate_schedule_id ? Number(form.default_rate_schedule_id) : null,
      }});
      onCreated();
    } catch (e) { setErr(parseErr(e, "Failed to create company")); setSaving(false); }
  };

  return (
    <Modal title="New Company" onClose={onClose} footer={<>
      <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving?"Saving...":"Create Company"}</button>
    </>}>
      {err && <div className="alert-error">{err}</div>}
      {!isAdmin && (
        <div style={{background:"#eff6ff",border:"1px solid #93c5fd",borderRadius:6,padding:"10px 12px",marginBottom:12,fontSize:12,color:"#1e40af"}}>
          New companies are submitted for admin review. You can still use this record on claims immediately — an admin will verify the details shortly.
        </div>
      )}
      <div className="form-grid">
        <div className="form-group full"><label>Company Name *</label><input value={form.company_name} onChange={e=>setForm({...form,company_name:e.target.value})} autoFocus/></div>
        <div className="form-group"><label>City</label><input value={form.city} onChange={e=>setForm({...form,city:e.target.value})}/></div>
        <div className="form-group"><label>State</label><input value={form.state} onChange={e=>setForm({...form,state:e.target.value})} placeholder="WA"/></div>
        <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})}/></div>
        <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})}/></div>
        <div className="form-group full">
          <label>Default Rate Schedule</label>
          <select value={form.default_rate_schedule_id} onChange={e=>setForm({...form,default_rate_schedule_id:e.target.value})}>
            <option value="">— None —</option>
            {schedules.map(s => <option key={s.id} value={s.id}>{s.name}{s.locks_rate ? " (locked)" : ""}</option>)}
          </select>
        </div>
        <div className="form-group full"><label>Notes</label><textarea rows={3} value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})}/></div>
      </div>
    </Modal>
  );
}
